import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Phone, Shield, Scale, Heart, TrendingUp, CheckCircle2, ArrowRight, MessageCircle } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const ICON_MAP = {
  TrendingUp: TrendingUp,
  Heart: Heart,
  Shield: Shield,
  Scale: Scale,
  Award: Shield,
};

const ServicesPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0E27]">
      {/* Header */}
      <header className="crystal-glass fixed top-0 left-0 right-0 z-50" data-testid="services-header">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex-shrink-0">
              <h1 className="font-heading text-xl md:text-2xl font-semibold text-[#D4AF37] tracking-tight">
                NOK <span className="text-white/40 font-light mx-1">|</span> <span className="text-white font-light text-base md:text-lg">Thailand Property</span>
              </h1>
            </Link>
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="text-[#94A3B8] hover:text-white hover:bg-white/5 rounded-full"
              data-testid="back-to-home-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('back')}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-16 px-6 lg:px-8" data-testid="services-hero">
        <div className="max-w-7xl mx-auto text-center">
          <p className="overline text-[#D4AF37] mb-4">{t('our_services')}</p>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-white tracking-tight mb-6">
            {t('services_title')}
          </h2>
          <p className="text-[#94A3B8] text-lg max-w-2xl mx-auto leading-relaxed">
            {t('services_desc')}
          </p>
        </div>
      </section>

      {/* Services */}
      <section className="px-6 lg:px-8 pb-24" data-testid="services-grid">
        <div className="max-w-5xl mx-auto">
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block h-10 w-10 animate-spin rounded-full border-2 border-solid border-[#D4AF37] border-r-transparent" />
            </div>
          ) : services.length === 0 ? (
            <div className="text-center py-20 bg-[#131A38] rounded-2xl border border-white/5">
              <Shield className="w-12 h-12 mx-auto text-[#D4AF37]/40 mb-4" />
              <p className="text-[#94A3B8]">Services coming soon</p>
            </div>
          ) : (
            <div className="space-y-6">
              {services.map((service) => {
                const Icon = ICON_MAP[service.icon] || Shield;
                return (
                  <div
                    key={service.id}
                    className="bg-[#131A38] border border-white/5 rounded-2xl p-6 md:p-8 hover:border-[#D4AF37]/20 transition-colors duration-300"
                    data-testid={`service-card-${service.id}`}
                  >
                    <div className="flex flex-col md:flex-row gap-6">
                      {/* Icon + Title */}
                      <div className="flex-shrink-0">
                        <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center">
                          <Icon className="w-6 h-6 text-[#D4AF37]" />
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading text-2xl font-semibold text-white mb-2" data-testid={`service-title-${service.id}`}>
                          {service.title}
                        </h3>
                        <p className="text-[#94A3B8] text-sm leading-relaxed mb-4">{service.description}</p>

                        {/* Features as compact pills */}
                        {service.features && service.features.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-5">
                            {service.features.map((feature, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center gap-1.5 text-xs text-[#94A3B8] bg-white/5 px-3 py-1.5 rounded-full"
                              >
                                <CheckCircle2 className="w-3 h-3 text-[#D4AF37]/60" />
                                {feature}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Contact row */}
                        <div className="flex items-center gap-4 pt-4 border-t border-white/5">
                          <span className="text-sm text-[#94A3B8]">{service.contact_name}</span>
                          {service.contact_phone && (
                            <a
                              href={`tel:${service.contact_phone}`}
                              className="inline-flex items-center gap-1.5 text-[#D4AF37] hover:text-[#E2C364] text-sm font-medium transition-colors duration-300"
                              data-testid={`service-phone-${service.id}`}
                            >
                              <Phone className="w-3.5 h-3.5" />
                              {service.contact_phone}
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 lg:px-8 bg-[#07091B] border-t border-white/5" data-testid="services-cta">
        <div className="max-w-3xl mx-auto text-center">
          <h3 className="font-heading text-3xl font-semibold text-white mb-4">Need Personalized Advice?</h3>
          <p className="text-[#94A3B8] mb-8">
            Our specialists are ready to help with insurance, legal, and investment consultation.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button
              onClick={() => navigate('/team')}
              className="bg-[#D4AF37] hover:bg-[#E2C364] text-[#0A0E27] font-semibold rounded-full px-8 h-12 text-sm"
              data-testid="meet-team-cta-btn"
            >
              Meet Our Team
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <a
              href="https://lin.ee/soB6jcI"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 h-12 bg-[#06C755] hover:bg-[#05b34d] text-white font-semibold text-sm rounded-full transition-colors duration-300"
              data-testid="services-line-realestate"
            >
              <MessageCircle className="w-4 h-4" />
              LINE — Real Estate
            </a>
            <a
              href="https://lin.ee/soB6jcI"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 h-12 bg-[#06C755]/80 hover:bg-[#06C755] text-white font-semibold text-sm rounded-full transition-colors duration-300"
              data-testid="services-line-legal"
            >
              <MessageCircle className="w-4 h-4" />
              LINE — Legal
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
